import type { PlatformSettingNameType } from '@/features/admin/platform-setup/constants'
import type { SettingType } from '@/features/admin/settings/types'
import fetchClient from '@/lib/fetch'

// Écrit plusieurs paramètres d'un coup (créés s'ils manquent) et rend la liste complète.
export async function upsertPlatformSettings(
  settings: Partial<Record<PlatformSettingNameType, string>>,
): Promise<SettingType[]> {
  return fetchClient('/v1/data/settings', {
    method: 'PATCH',
    body: JSON.stringify({ settings }),
  })
}

// Plateforme vierge : crée l'organisation, le premier compte (admin) et le premier programme.
export async function createFirstAdmin(payload: {
  organizationName: string
  firstname: string
  lastname: string
  email: string
  password: string
}): Promise<{ token: string }> {
  return fetchClient('/v1/data/platform-setup/first-admin', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

// Envoie le logo choisi sur l'ordinateur : l'API le dépose sur Scaleway et rend son adresse publique.
export async function uploadPlatformLogo(file: File): Promise<{ url: string }> {
  const body = new FormData()
  body.append('file', file)
  return fetchClient('/v1/data/settings/logo', { method: 'POST', body }, true)
}

// Apparence déduite du site web de l'organisation : logo recopié sur Scaleway et couleurs.
export async function getBrandBranding(
  url: string,
): Promise<{ logo: string | null; colorPrimary: string | null; colorSecondary: string | null }> {
  return fetchClient(`/v1/data/brand-info/branding?url=${encodeURIComponent(url)}`, { method: 'GET' })
}

export type SuperAdminType = {
  id: string
  email: string
  firstname: string
  lastname: string
  /** Invité, compte pas encore activé. */
  pending: boolean
  invitedAt: string | null
  lastLoginAt: string | null
  isCurrentUser: boolean
  /** Propriétaire de la plateforme : personne ne peut le retirer. */
  isOwner: boolean
  /** Équipe Constel : visible seulement par l'équipe Constel elle-même. */
  isConstelStaff: boolean
}

// Super admins : ADMIN + SUPPORT sur tous les programmes, absents des listes d'utilisateurs.
export async function getSuperAdmins(): Promise<{ superAdmins: SuperAdminType[]; canTransfer: boolean }> {
  return fetchClient('/v1/data/super-admins', { method: 'GET' })
}

export async function inviteSuperAdmins(emails: string[]): Promise<{ failed: string[] }> {
  return fetchClient('/v1/data/super-admins', { method: 'POST', body: JSON.stringify({ emails }) })
}

export async function resendSuperAdminInvitation(userId: string): Promise<{ message: string }> {
  return fetchClient(`/v1/data/super-admins/${userId}/invitation`, { method: 'POST' })
}

export async function removeSuperAdmin(userId: string): Promise<{ message: string }> {
  return fetchClient(`/v1/data/super-admins/${userId}`, { method: 'DELETE' })
}

// Seul le propriétaire (ou l'équipe Constel, en dépannage) transfère la propriété.
export async function transferOwnership(userId: string): Promise<{ message: string }> {
  return fetchClient('/v1/data/super-admins/owner', { method: 'POST', body: JSON.stringify({ userId }) })
}
